import { useFormik } from "formik";
import { Calendar } from "primereact/calendar";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDownload } from "react-icons/md";
import Select from "react-select";
import * as Yup from "yup";

import { ReactComponent as ClassroomReportsIcon } from "../../../assets/Subtraction3.svg";
import Button from "../../../components/Button";
import { useTheme } from "../../../context/ThemeContext";
import classroomServices from "../../../services/classroomServices";
import schoolServices from "../../../services/schoolServices";
import teacherServices from "../../../services/teacherServices";
import { selectStylesV2 } from "../../../styles/selectStyles";

const ClassroomReports = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [teachers, setTeachers] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);

  const formik = useFormik({
    initialValues: {
      selectedTeacher: null,
      selectedClassroom: null,
      fromDate: "",
      toDate: "",
      lifetimeReport: false,
    },
    validationSchema: Yup.object().shape({
      selectedTeacher: Yup.object().nullable().required(t("")),
      selectedClassroom: Yup.object().nullable().required(t("")),
      fromDate: Yup.date()
        .nullable()
        .when("lifetimeReport", {
          is: false,
          then: (schema) => schema.required(t("")),
        }),
      toDate: Yup.date()
        .nullable()
        .test("required-if-dated", t(""), function (value) {
          const { fromDate, lifetimeReport } = this.parent;
          if (!lifetimeReport && fromDate) return !!value;
          return true;
        })
        .test("end-after-start", t("validation.error_end_date_before_start_date"), function (value) {
          const { fromDate, lifetimeReport } = this.parent;
          if (!lifetimeReport && fromDate && value) return value >= fromDate;
          return true;
        }),
    }),
    validateOnChange: true,
    validateOnBlur: true,
  });

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const response = await schoolServices.getTeachersBySchool(null);

      const options =
        response?.data?.map((teacher) => ({
          id: teacher.id,
          value: teacher.id.toString(),
          label: teacher.name,
        })) || [];

      setTeachers(options);
    } catch (error) {
      console.error("Failed to fetch getTeachersBySchool:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassrooms = async (teacherId) => {
    setLoadingClassrooms(true);
    if (!teacherId) return;
    try {
      const res = await teacherServices.getClassroomsByTeacher(teacherId);
      const options =
        res?.data?.map((cls) => ({
          id: cls.id,
          value: cls.id.toString(),
          label: cls.name + " - " + cls.section,
        })) || [];
      setClassrooms(options);
    } catch (error) {
      console.error("Failed to fetch classrooms:", error);
    } finally {
      setLoadingClassrooms(false);
    }
  };

  const fetchReportBlob = async () => {
    const { selectedClassroom, fromDate, toDate, lifetimeReport } = formik.values;
    if (!selectedClassroom) return null;

    const payload = {
      start_date: lifetimeReport ? null : fromDate,
      end_date: lifetimeReport ? null : toDate,
    };

    try {
      return await classroomServices.exportClassroomReport(selectedClassroom.id, payload);
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const handlePreview = async () => {
    await formik.validateForm();
    if (!formik.isValid) return;

    setPreviewLoading(true);
    const blob = await fetchReportBlob();
    if (blob) {
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    }
    setPreviewLoading(false);
  };

  const handleDownload = async () => {
    await formik.validateForm();
    if (!formik.isValid) return;

    setDownloadLoading(true);
    const blob = await fetchReportBlob();
    if (blob) {
      const classroomName = formik.values.selectedClassroom?.label || "report";
      const filename = `${classroomName.replace(/\s+/g, "_")}_report.xlsx`;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    setDownloadLoading(false);
  };

  const toggleLifetimeReport = () => {
    const lifetime = !formik.values.lifetimeReport;
    formik.setValues({
      ...formik.values,
      lifetimeReport: lifetime,
      fromDate: lifetime ? "" : formik.values.fromDate,
      toDate: lifetime ? "" : formik.values.toDate,
    });
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  useEffect(() => {
    formik.setFieldValue("selectedClassroom", null);
    fetchClassrooms(formik.values.selectedTeacher?.id);
  }, [formik.values.selectedTeacher]);

  const isSubmitDisabled =
    !formik.isValid ||
    !formik.values.selectedTeacher ||
    !formik.values.selectedClassroom ||
    (!formik.values.lifetimeReport && (!formik.values.fromDate || !formik.values.toDate));

  return (
    <div className="flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-3xl rounded-xl bg-white p-8 shadow-md dark:bg-dark-surface">
        {/* Header */}
        <div className="flex items-center space-x-4 border-b pb-6 dark:border-dark-background">
          <ClassroomReportsIcon className="h-14 w-14 text-blue-500" />
          <h1 className="font-semibold text-blue-500 md:text-2xl">{t("classroom_reports")}</h1>
        </div>

        {/* Select Teacher */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-mutedText dark:text-dark-mutedText">
            {t("select_teacher")}
          </label>
          <Select
            isLoading={loading}
            options={teachers}
            value={formik.values.selectedTeacher}
            onChange={(val) => formik.setFieldValue("selectedTeacher", val)}
            placeholder={t("select_teacher")}
            styles={selectStylesV2(theme)}
            noOptionsMessage={() => <p>{t("no_options")}</p>}
            loadingMessage={() => <p>{t("loading")}</p>}
            onBlur={() => formik.setFieldTouched("selectedTeacher", true)}
          />
          {formik.touched.selectedTeacher && formik.errors.selectedTeacher && (
            <div className="text-sm text-red-500">{formik.errors.selectedTeacher}</div>
          )}
        </div>

        {/* Select Classroom */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-mutedText dark:text-dark-mutedText">
            {t("select_classroom")}
          </label>
          <Select
            isLoading={loadingClassrooms}
            options={classrooms}
            value={formik.values.selectedClassroom}
            onChange={(val) => formik.setFieldValue("selectedClassroom", val)}
            placeholder={t("select_classroom")}
            styles={selectStylesV2(theme)}
            noOptionsMessage={() => <p>{t("no_options")}</p>}
            loadingMessage={() => <p>{t("loading")}</p>}
            onBlur={() => formik.setFieldTouched("selectedClassroom", true)}
            isDisabled={!formik.values.selectedTeacher}
          />
          {formik.touched.selectedClassroom && formik.errors.selectedClassroom && (
            <div className="text-sm text-red-500">{formik.errors.selectedClassroom}</div>
          )}
        </div>

        {/* Date Pickers */}
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {["fromDate", "toDate"].map((field) => (
            <div key={field}>
              <label className="block text-sm font-medium text-mutedText dark:text-dark-mutedText">
                {t(field === "fromDate" ? "from_date" : "to_date")}
              </label>
              <Calendar
                className="w-full"
                dateFormat="dd/mm/yy"
                value={formik.values[field]}
                disabled={formik.values.lifetimeReport}
                placeholder={t("select_date")}
                onChange={(e) => formik.setFieldValue(field, e.value)}
                onBlur={() => formik.setFieldTouched(field, true)}
                showIcon
                pt={{
                  day: "p-0 text-sm",
                  root: "w-full rounded-md border bg-[#F9FAFB] px-3 py-2 focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background",
                  input:
                    "p-0 text-sm placeholder:text-sm placeholder:text-mutedText dark:text-dark-mutedText dark:text-white",
                }}
              />
              {formik.touched[field] && formik.errors[field] && (
                <div className="text-sm text-red-500">{formik.errors[field]}</div>
              )}
            </div>
          ))}
        </div>

        {/* Lifetime Report */}
        <div className="mt-4 flex items-center space-x-3">
          <input
            type="checkbox"
            id="lifetime-report"
            checked={formik.values.lifetimeReport}
            onChange={toggleLifetimeReport}
            className="h-5 w-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
          />
          <label htmlFor="lifetime-report" className="text-sm text-mutedText dark:text-dark-mutedText">
            {t("generate_lifetime_report")}
          </label>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end space-x-2">
          <Button
            className="min-w-36"
            label={t("download")}
            loading={downloadLoading}
            onClick={handleDownload}
            iconLeft={<MdDownload size={22} />}
            disabled={downloadLoading || isSubmitDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export default ClassroomReports;
